import { ChevronRight, Home } from "lucide-react";
import { Link, useLocation } from "@/lib/router-compat";

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
}

export function Breadcrumbs({ items }: BreadcrumbsProps) {
  const location = useLocation();
  
  // Auto-generate breadcrumbs from URL if not provided
  const breadcrumbItems = items || generateBreadcrumbsFromPath(location.pathname);
  
  return (
    <nav aria-label="Breadcrumb" className="py-4">
      <ol className="flex flex-wrap items-center gap-2 text-sm">
        <li>
          <Link 
            to="/" 
            className="flex items-center text-muted-foreground hover:text-seo-bright transition-colors"
            aria-label="Inicio"
          >
            <Home size={16} />
          </Link>
        </li>
        
        {breadcrumbItems.map((item, index) => (
          <li key={`${item.path}-${index}`} className="flex items-center space-x-2">
            <ChevronRight size={16} className="text-muted-foreground" />
            {index === breadcrumbItems.length - 1 ? (
              <span className="text-foreground font-medium" aria-current="page">
                {item.label}
              </span>
            ) : (
              <Link 
                to={item.path} 
                className="text-muted-foreground hover:text-seo-bright transition-colors"
              >
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function generateBreadcrumbsFromPath(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [];
  
  const pathMap: Record<string, string> = {
    'blog': 'Conocimiento',
    'servicios': 'Servicios',
    'seo-local-ia': 'SEO Local con IA',
    'seo-web-ia': 'SEO Web con IA',
    'seo-ecommerce-ia': 'SEO eCommerce con IA',
    'gestion-resenas-ia': 'Gestión de Reseñas con IA',
    'link-building-inteligente': 'Link Building Inteligente',
    'publicidad-sem-ia': 'Publicidad SEM con IA',
    'creacion-web-react': 'Creación Web con React',
    'creacion-ecommerce-shopify': 'Creación eCommerce con Shopify',
    'google-merchant-ia': 'Google Merchant con IA',
    'agenda-consultoria': 'Agenda una Consultoría',
    'zonas-exclusivas': 'Zonas Exclusivas',
    'legal': 'Legal',
    'terminos-servicio': 'Términos de Servicio',
    'privacidad': 'Política de Privacidad',
    'cookies': 'Política de Cookies',
    'condiciones-uso': 'Condiciones de Uso',
  };
  
  let currentPath = '';
  segments.forEach((segment) => {
    currentPath += `/${segment}`;
    const label = pathMap[segment] || segment.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    
    breadcrumbs.push({
      label,
      path: currentPath,
    });
  });
  
  return breadcrumbs;
}
